import { useEffect, useState } from "react";
import { Typography } from "@material-tailwind/react";
import { useKeycloak } from "@react-keycloak/web";

/**
 * Mapping des realms vers les informations des pays
 */
const realmToCountry: { [key: string]: { name: string; flagPath: string } } = {
  "be": { name: "Belgium", flagPath: "/assets/flags/be.svg" },
  // "ro": { name: "Romania", flagPath: "/assets/flags/ro.svg" },
  "pl": { name: "Poland", flagPath: "/assets/flags/pl.svg" },
  "lu": { name: "Luxembourg", flagPath: "/assets/flags/lu.svg" }
};

/**
 * CountryIndicator: Affiche le pays actuel avec son drapeau
 * Utilise le realm stocké dans localStorage pour déterminer le pays
 */
export default function CountryIndicator() {
  const [realm, setRealm] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { keycloak } = useKeycloak();

  const isSuperAdmin = keycloak.realmAccess?.roles.includes("ROLE_SUPER_ADMIN");

  useEffect(() => {
    // Récupérer le realm depuis le localStorage
    const currentRealm = localStorage.getItem("realm");
    setRealm(currentRealm);

    const handleStorageChange = () => {
      const newRealm = localStorage.getItem("realm");
      setRealm(newRealm);
    };

    window.addEventListener("storage", handleStorageChange);
    
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      if (key === "realm") {
        setRealm(value);
      }
      return originalSetItem.apply(this, [key, value]);
    };

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  // Fermer le menu quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".country-indicator")) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  /**
   * Change le realm et redémarre l'authentification
   * @param newRealm - Nouveau realm à utiliser
   */
  const handleRealmChange = async (newRealm: string) => {
    if (newRealm === realm) return;

    try {
      // Sauvegarder le nouveau realm
      localStorage.setItem("realm", newRealm);
      setRealm(newRealm);

      // Essayer d'abord un changement "doux" - rechargement de la page
      window.location.reload();
      
    } catch (error) {
      console.error("Erreur lors du changement de realm:", error);
      // En cas d'erreur, forcer la déconnexion et redirection
      if (keycloak.authenticated) {
        await keycloak.logout();
      }
      window.location.href = "/";
    }
  };

  if (!realm || !realmToCountry[realm]) {
    return null;
  }

  const countryInfo = realmToCountry[realm];

  return (
    <div className="country-indicator flex items-center gap-2">
      <Typography variant="small" className="text-gray-700 font-semibold">
        {countryInfo.name}
      </Typography>
      <img 
        src={countryInfo.flagPath} 
        alt={`${countryInfo.name} flag`}
        className={`w-8 h-8 rounded-full border border-gray-300 object-cover transition-transform duration-200 ${
          isSuperAdmin 
            ? "cursor-pointer hover:scale-110" 
            : "cursor-default"
        }`}
        onClick={() => isSuperAdmin && setIsOpen(!isOpen)}
      />
      
      {isOpen && isSuperAdmin && (
        <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[150px] overflow-hidden">
          {Object.entries(realmToCountry).map(([realmKey, country], index) => (
            <div
              key={realmKey}
              className={`country-option flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-all duration-300 ${
                realmKey === realm ? "bg-blue-50 text-blue-700" : ""
              }`}
              style={{
                animationDelay: `${index * 100}ms`,
                animation: "slideInFromTop 0.4s ease-out forwards",
                opacity: 0,
                transform: "translateY(-25px)"
              }}
              onClick={() => {
                handleRealmChange(realmKey);
                setIsOpen(false);
              }}
            >
              <img 
                src={country.flagPath} 
                alt={`${country.name} flag`}
                className="w-5 h-5 rounded-full object-cover"
              />
              <Typography variant="small" className="font-medium">
                {country.name}
              </Typography>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
/***/
